import React, { useState } from 'react';

function DeliveryRoute({ route, onDeliveryComplete, onBackToActive }) {
  const [currentStop, setCurrentStop] = useState(0);
  const [deliveryCode, setDeliveryCode] = useState('');
  const [error, setError] = useState('');
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  const handleStartDelivery = (delivery, index) => {
    setSelectedDelivery(delivery);
    setCurrentStop(index);
    setShowDeliveryModal(true);
    setDeliveryCode('');
    setError('');
  };

  const handleCompleteDelivery = (e) => {
    e.preventDefault();
    setError('');

    if (!deliveryCode || deliveryCode.length !== 6) {
      setError('Please enter a valid 6-digit delivery code');
      return;
    }

    const delivery = route.deliveries[currentStop];
    const success = onDeliveryComplete(delivery.id, deliveryCode);

    if (!success) {
      setError('Incorrect delivery code. Please check with the customer.');
      return;
    }

    // Success
    setShowDeliveryModal(false);
    setDeliveryCode('');
    setSelectedDelivery(null);
  };

  const openMaps = (address) => {
    const encodedAddress = encodeURIComponent(address);
    // Try to open in Google Maps app first, fallback to web
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
  };

  return (
    <div className="delivery-route">
      <div className="route-header">
        <button className="btn-back" onClick={onBackToActive}>
          ← Back
        </button>
        <h2>Delivery Route</h2>
        <div className="route-stats-mini">
          {route.deliveries.length} stops
        </div>
      </div>

      {/* Route Overview */}
      <div className="route-overview">
        <div className="overview-card">
          <div className="overview-icon">📍</div>
          <div className="overview-content">
            <div className="overview-value">{route.deliveries.length}</div>
            <div className="overview-label">Total Stops</div>
          </div>
        </div>
        <div className="overview-card">
          <div className="overview-icon">🛣️</div>
          <div className="overview-content">
            <div className="overview-value">{route.totalDistance.toFixed(1)} km</div>
            <div className="overview-label">Distance</div>
          </div>
        </div>
        <div className="overview-card">
          <div className="overview-icon">⏱️</div>
          <div className="overview-content">
            <div className="overview-value">{route.estimatedTime} min</div>
            <div className="overview-label">Est. Time</div>
          </div>
        </div>
      </div>

      {/* Route Map Placeholder */}
      <div className="route-map-container">
        <div className="route-map-placeholder">
          <div className="map-icon">🗺️</div>
          <p>Interactive map would display here</p>
          <p className="map-note">Showing optimized route with {route.deliveries.length} stops</p>
        </div>
      </div>

      {/* Delivery Stops */}
      <div className="route-stops">
        <h3>Delivery Stops</h3>
        <div className="stops-list">
          {route.deliveries.map((delivery, index) => (
            <div 
              key={delivery.id}
              className={`stop-card ${index === currentStop ? 'current' : ''}`}
            >
              <div className="stop-number">{index + 1}</div>
              
              <div className="stop-content">
                <div className="stop-header">
                  <div className="stop-title">
                    <span className="order-id">{delivery.orderId}</span>
                    <span className="package-code">{delivery.packageCode}</span>
                  </div>
                  <div className="stop-payment">€{delivery.payment.toFixed(2)}</div>
                </div>

                <div className="stop-customer">
                  <div className="customer-info">
                    <span className="customer-icon">👤</span>
                    <span className="customer-name">{delivery.customerName}</span>
                  </div>
                  <a href={`tel:${delivery.customerPhone}`} className="btn-call">
                    📞 Call
                  </a>
                </div>

                <div className="stop-address">
                  <span className="address-icon">📍</span>
                  <span className="address-text">{delivery.deliveryAddress}</span>
                </div>

                <div className="stop-details">
                  <span className="detail-badge">
                    🛣️ {delivery.distance} km
                  </span>
                  <span className="detail-badge">
                    ⏱️ ~{delivery.estimatedTime} min
                  </span>
                </div>

                <div className="stop-actions">
                  <button 
                    className="btn-navigate"
                    onClick={() => openMaps(delivery.deliveryAddress)}
                  >
                    🧭 Navigate
                  </button>
                  <button 
                    className="btn-deliver"
                    onClick={() => handleStartDelivery(delivery, index)}
                  >
                    ✓ Mark Delivered
                  </button>
                </div>
              </div>

              <div className="stop-connector"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery Completion Modal */}
      {showDeliveryModal && selectedDelivery && (
        <div className="modal-overlay" onClick={() => setShowDeliveryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Complete Delivery</h3>
              <button 
                className="btn-close"
                onClick={() => setShowDeliveryModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="delivery-summary">
                <div className="summary-row">
                  <span className="label">Order:</span>
                  <span className="value">{selectedDelivery.orderId}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Package:</span>
                  <span className="value">{selectedDelivery.packageCode}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Customer:</span>
                  <span className="value">{selectedDelivery.customerName}</span>
                </div>
                <div className="summary-row">
                  <span className="label">Payment:</span>
                  <span className="value">€{selectedDelivery.payment.toFixed(2)}</span>
                </div>
              </div>

              <form onSubmit={handleCompleteDelivery}>
                <div className="form-group">
                  <label htmlFor="delivery-code">
                    Enter Delivery PIN Code
                  </label>
                  <p className="helper-text">
                    Ask the customer for their 6-digit delivery code
                  </p>
                  <input
                    id="delivery-code"
                    type="text"
                    value={deliveryCode}
                    onChange={(e) => setDeliveryCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    maxLength="6"
                    className="code-input"
                    autoFocus
                    required
                  />
                  <p className="code-hint">
                    Customer code: {selectedDelivery.deliveryCode}
                    <span className="hint-note">(for demo purposes)</span>
                  </p>
                </div>

                {error && (
                  <div className="error-message">{error}</div>
                )}

                <div className="modal-actions">
                  <button 
                    type="button"
                    className="btn-cancel"
                    onClick={() => setShowDeliveryModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="btn-confirm"
                  >
                    Confirm Delivery
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryRoute;
